import { useCallback, useEffect, useMemo, useState } from 'react';

import { SearchResponse } from 'instantsearch.js';

import { searchClient } from '@/modules/product/components/AlgoliaSearch/AlgoliaProvider';
import { PRODUCT_INDEX } from '@/modules/product/components/collections/constants';
import { AlgoliaHit } from '@/modules/product/models/algolia-hit.interface';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import { mapAlgoliaProductToClient } from '@/modules/product/services/productTransform';
import { usePrevious } from '@/modules/visualizer/utils/utils';

type UseSkuSearchOptions = {
  disabled?: boolean;
  extraFilters?: string;
  hitsPerPage?: number;
  page?: number;
  query?: string;
};
export const useSkuSearch = (skus: string[], options?: UseSkuSearchOptions) => {
  const [products, setProducts] = useState<ProductPdpInterface[]>([]);
  const [hasMoreHits, setHasMoreHits] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const prevSkus = usePrevious(skus);
  const prevOptions = usePrevious(options);
  const hasSkusChanged = JSON.stringify(skus) !== JSON.stringify(prevSkus);
  const hasOptionsChanged = JSON.stringify(options) !== JSON.stringify(prevOptions);
  const searchDisabled = !!options?.disabled;

  const handleSearch = useCallback(async () => {
    if (searchDisabled || isPending || skus.length === 0) {
      return;
    }

    setIsPending(true);

    const buildFilters = () => {
      let filtersStr = [...skus.map((sku) => `sku:${sku}`)].join(' OR ');

      if (options?.extraFilters) {
        filtersStr = `${options.extraFilters} AND (${filtersStr})`;
      }

      return filtersStr;
    };

    const getQuery = () => {
      if (options?.query) {
        return options.query;
      }

      if (skus.length === 1) {
        return skus[0];
      }

      return '';
    };

    const skuIndexSearch = await searchClient.search([
      {
        indexName: PRODUCT_INDEX,
        params: {
          analytics: false,
          query: getQuery(),
          filters: buildFilters(),
          hitsPerPage: options?.hitsPerPage ? options.hitsPerPage : skus.length + 1,
          page: options?.page ? options.page : 0,
          distinct: false,
        },
      },
    ]);

    if (!skuIndexSearch?.results?.[0]) {
      setIsPending(false);
      setHasSearched(true);
      return;
    }

    const { hits, nbPages, page } = skuIndexSearch.results[0] as SearchResponse<AlgoliaHit>;

    const mappedHits: (ProductPdpInterface | null)[] = hits.map((hit) =>
      mapAlgoliaProductToClient(hit as unknown as AlgoliaHit)
    );

    // order the products in the same order as the skus
    const orderedProducts: ProductPdpInterface[] = skus
      .map((sku) => mappedHits.find((product) => product?.sku === sku))
      .filter((hit): hit is ProductPdpInterface => hit !== null && !!hit);

    setProducts(orderedProducts);
    setIsPending(false);
    setHasSearched(true);

    setHasMoreHits(nbPages > page + 1);
    return;
  }, [isPending, options, searchDisabled, skus]);

  useEffect(() => {
    if (!!hasSkusChanged || !!hasOptionsChanged || !hasSearched) {
      handleSearch();
    }
  }, [hasSkusChanged, hasSearched, hasOptionsChanged, handleSearch]);

  // Filter products to only include those that are published
  const validProducts = useMemo(() => {
    return products.filter((product: ProductPdpInterface) => {
      return Boolean(product?.published);
    });
  }, [products]);

  return {
    products,
    validProducts, // Only published products (from Algolia)
    // Report pending while the first search hasn't finished, so callers don't render
    // an empty grid in the gap between mount and the first searchClient resolution
    // (which now also covers the wait for the shipping region to be resolved).
    isPending: isPending || (!hasSearched && skus.length > 0 && !searchDisabled),
    hasMoreHits,
  };
};
